import styled from 'styled-components'
import PropTypes from 'prop-types'
import { Children, cloneElement, useState, useCallback, useEffect } from 'react'

import { features } from '@h2/components/media'
import { vagRundFont, base } from '@h2/components/mixins'
import { PG90T, PB40T } from '@h2/components/colors'

const Input = styled.input`
  && {
    display: none;
  }
`
const Label = styled.label`
  ${base};
  color: black;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 15px;
  border: none;
  outline-color: black;
  outline-style: solid;
  outline-width: 1px;
  border-radius: 5px;
  background-color: ${({ backgroundColor }) => backgroundColor};
  transition: all 0.25s ease-in-out;

  > span[role='img'] {
    cursor: pointer;
    align-self: flex-start;
  }

  > span.flex {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 5px;

    > span:first-child {
      ${vagRundFont};
      font-size: 20px;
      font-weight: 400;
      word-break: break-word;
      letter-spacing: 0.6px;
    }

    > span:last-child {
      font-size: 14px;
      font-weight: 300;
      color: ${PB40T};
    }
  }

  &:hover,
  &.selected {
    outline-width: 3px;
    background-color: ${({ backgroundHoverColor }) => backgroundHoverColor};
  }

  &:focus-visible {
    outline-width: 3px;
    outline-color: blue;
  }

  @media (${features.minWidth.laptop}) {
    flex-direction: column;
    gap: 5px;
    padding: 20px 10px;

    > span[role='img'] {
      align-self: auto;
    }

    > span.flex {
      display: contents;
      text-align: center;
    }
  }
`
const Fieldset = styled.fieldset`
  display: flex;
  gap: 15px;

  > ${Label} {
    flex: 1 1 ${({ size }) => 100 / size}%;
    height: ${({ height }) => height};
  }
`
const RadioButton = ({
  children,
  value,
  color = 'white',
  hoverColor = PG90T,
  ...rest
}) => {
  return (
    <Label
      tabIndex="0"
      backgroundColor={color}
      backgroundHoverColor={hoverColor}
      className={rest.selected ? 'selected' : null}>
      {children}
      <Input type="radio" name={rest.name} value={value} />
    </Label>
  )
}
const RadioButtonGroup = ({ children, value, name, onChange, height = 'auto' }) => {
  const [selected, setSelected] = useState(value ?? '')
  const handleOnChange = useCallback(
    evt => {
      setSelected(evt.target.value)

      if (typeof onChange === 'function') {
        onChange(evt, evt.target.value)
      }
    },
    [onChange]
  )

  useEffect(() => {
    setSelected(value)
  }, [value])

  return (
    <Fieldset height={height} size={Children.count(children)} onChange={handleOnChange}>
      {Children.map(children, radio => {
        const props = { name }

        if (radio.props?.value === selected) {
          props.selected = true
        }

        return cloneElement(radio, { ...radio.props, ...props })
      })}
    </Fieldset>
  )
}

RadioButton.propTypes = {
  /** The value of the radio component */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /** The background color of the button */
  color: PropTypes.string,
  /** The hover color of the background of the button*/
  hoverColor: PropTypes.string
}
RadioButtonGroup.propTypes = {
  /** The value of the radio group */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** The name of the radio group */
  name: PropTypes.string.isRequired,
  /** Callback when the group value changes */
  onChange: PropTypes.func,
  /** The height of radio options within the group */
  height: PropTypes.string
}

export { RadioButton, RadioButtonGroup }
